import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Spin, Typography } from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import message from "@/utils/globalMessage";
import { api } from "@/redux/api";
import { buildFacilityPath, buildPortfolioPath } from "@/utils/route-context";
import ContractRequestForm from "./ContractRequestForm";
import {
  CreatePage,
  CreateHeader,
  HeaderIdentity,
  HeaderActions,
  FormPanel,
  FormCard,
} from "./ContractRequestCreate.styles";
import {
  CONTRACT_CREATE_PREFILL_KEY,
  clearFlowPrefill,
  readAndClearFlowPrefill,
} from "@/utils/flow-prefill";

const getRows = (response) => {
  const payload = response?.data;
  return Array.isArray(payload) ? payload : payload?.results || [];
};

export default function ContractRequestCreatePage({ scope = "facility" }) {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId, facilityId } = useParams();
  const selectedFacility = useSelector((state) => state.Facility?.selected);
  const userFacilities = useSelector((state) => state.Auth?.user_facilities);
  const [facilities, setFacilities] = useState([]);
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSource, setLoadingSource] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const facilityContext = useMemo(() => ({
    companyId,
    facilityId,
  }), [companyId, facilityId]);

  const listPath = useMemo(() => {
    if (scope === "portfolio") return buildPortfolioPath("contract-request", { companyId }) || "/switch-view";
    if (scope === "contract") return "/contract/contract-request";
    return buildFacilityPath("contract-request", facilityContext) || "/switch-view";
  }, [companyId, facilityContext, scope]);

  useEffect(() => {
    let active = true;
    const loadFacilities = async () => {
      try {
        let rows;
        if (scope === "facility") {
          const selectedId = selectedFacility?.Id ?? selectedFacility?.id;
          const selectedName = String(selectedId) === String(facilityId)
            ? selectedFacility?.name || selectedFacility?.Name
            : undefined;
          rows = facilityId
            ? [{
              id: Number(facilityId),
              name: selectedName || `Facility #${facilityId}`,
            }]
            : [];
        } else if (scope === "portfolio") {
          rows = (Array.isArray(userFacilities) ? userFacilities : [])
            .filter((item) => String(item.company_id) === String(companyId))
            .map((item) => ({ id: item.id, name: item.name }));
        } else {
          rows = getRows(await api.getContractFacilities());
        }

        if (active) setFacilities(rows);
      } catch {
        if (active) message.error("Failed to load facilities");
      }
    };

    loadFacilities().finally(() => {
      if (active) setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [companyId, facilityId, scope, selectedFacility, userFacilities]);

  useEffect(() => {
    const sessionPrefill = readAndClearFlowPrefill(CONTRACT_CREATE_PREFILL_KEY);
    if (sessionPrefill) {
      setInitialValues(sessionPrefill);
      setLoadingSource(false);
      return undefined;
    }

    const params = new URLSearchParams(location.search);
    const source = params.get("source");
    const sourceId = Number(params.get("source_id"));
    if (!source || !sourceId) {
      setLoadingSource(false);
      return undefined;
    }

    let active = true;
    setLoadingSource(true);
    const loader = source === "workorder"
      ? api.getWorkorderById(sourceId)
      : api.getFacilityServiceRequest(sourceId);
    loader
      .then((response) => {
        if (!active) return;
        const record = response?.data || response;
        setInitialValues({
          sourceLabel: source === "workorder"
            ? `Work Order #${record.workordernumber || sourceId}`
            : `Tenant Request #${sourceId}`,
          workorder: source === "workorder" ? sourceId : undefined,
          service_request: source === "tenant-request" ? sourceId : undefined,
          facility: Number(record.facility?.id || record.facility || facilityId),
          equipment: record.equipment?.id || record.equipment || undefined,
          title: record.issue || record.title,
          description: record.description,
          priority: record.priority,
          due_date: record.due_date,
          service_category:
            record.service_category ||
            record.category ||
            record.service_request_summary?.category,
        });
      })
      .catch(() => {
        if (active) message.error("Failed to load request details");
      })
      .finally(() => {
        if (active) setLoadingSource(false);
      });

    return () => {
      active = false;
    };
  }, [facilityId, location.search]);

  useEffect(() => () => clearFlowPrefill(CONTRACT_CREATE_PREFILL_KEY), []);

  const submit = async (values) => {
    setSubmitting(true);
    try {
      const payload = {
        ...values,
        due_date: values.due_date ? values.due_date.format("YYYY-MM-DD") : null,
        bid_due_date: values.bid_due_date ? values.bid_due_date.format("YYYY-MM-DD") : null,
      };
      const response = await api.createContract(payload);
      clearFlowPrefill(CONTRACT_CREATE_PREFILL_KEY);
      message.success("Contract request created");
      const requestId = response?.data?.id;
      if (!requestId) {
        navigate(listPath, { replace: true });
        return;
      }
      const detailPath = scope === "portfolio"
        ? buildPortfolioPath(`contract-request-detail/${requestId}`, { companyId })
        : scope === "contract"
          ? `/contract/contract-request-detail/${requestId}`
          : buildFacilityPath(`contract-request-detail/${requestId}`, facilityContext);
      navigate(detailPath || listPath, { replace: true });
    } catch (error) {
      const data = error?.response?.data;
      message.error(data?.detail || Object.values(data || {})[0] || "Failed to create contract request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    clearFlowPrefill(CONTRACT_CREATE_PREFILL_KEY);
    navigate(listPath);
  };

  const title = initialValues?.sourceLabel
    ? `Create Contract from ${initialValues.sourceLabel}`
    : "Create Contract Request";

  return (
    <CreatePage>
      <CreateHeader>
        <HeaderIdentity>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleCancel}
            title="Back"
            aria-label="Back"
          />
          <Typography.Title level={4}>{title}</Typography.Title>
        </HeaderIdentity>
        <HeaderActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={submitting}
            onClick={() => form.submit()}
          >
            Create Request
          </Button>
        </HeaderActions>
      </CreateHeader>

      <Spin spinning={loading || loadingSource}>
        <FormPanel>
          <FormCard>
            <ContractRequestForm
              form={form}
              facilityIds={facilities.map((item) => item.id)}
              facilities={facilities}
              initialValues={initialValues}
              onFinish={submit}
            />
          </FormCard>
        </FormPanel>
      </Spin>
    </CreatePage>
  );
}
